import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Gleap from 'gleap';

import i18n from '@/shared/i18n/i18n';
import AuthLayout from '@/components/AppComponents/Layout/AuthLayout';
import {
  LoginFormValues,
  loginSchema,
  OtpFormValues,
  otpSchema,
} from './schemas/login.schema';
import {
  useLoginUseCase,
  useValidateOtpUseCase,
} from '@/domains/auth/auth.usecases';
import { useAuthStore } from '@/domains/auth/auth.store';
import { Form } from '@/components/AppComponents/Form/Form';
import { TextInput } from '@/components/UI/components/Textfield';
import { Button } from '@/components/UI/components/Button';
import { NAVBAR_SOURCES } from '@/components/AppComponents/Sidebar/NavBar/NavBarSources';
import { Callout } from '@/components/UI/components/Callout';
import { RiErrorWarningLine } from '@remixicon/react';

const LoginPage: React.FC = () => {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [isLoadingLogIn, setIsLoadingLogIn] = useState<boolean>(false);
  const [isLoadingOtp, setIsLoadingOtp] = useState<boolean>(false);
  const [remainingTime, setRemainingTime] = useState<number>(0); // Countdown time
  const [credentials, setCredentials] = useState<LoginFormValues | null>(null);
  const [showSessionExpiredMessage, setShowSessionExpiredMessage] =
    useState<boolean>(false);
  const resendCooldown = 60;

  const loginUseCase = useLoginUseCase();
  const validateOtpUseCase = useValidateOtpUseCase();
  const { setTokens } = useAuthStore();

  const navigate = useNavigate();

  Gleap.showFeedbackButton(true);

  const loginMethods = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
  });

  const otpMethods = useForm<OtpFormValues>({
    resolver: zodResolver(otpSchema),
  });

  const handleLogin = async (data: LoginFormValues) => {
    try {
      setIsLoadingLogIn(true);
      await loginUseCase(data);
      setCredentials(data);
      setIsLoggedIn(true);
      setRemainingTime(resendCooldown);
    } catch (error) {
      loginMethods.setValue('password', '', {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
      loginMethods.setFocus('password');
      throw error;
    } finally {
      setIsLoadingLogIn(false);
    }
  };

  const handleOtpSubmit = async (data: OtpFormValues) => {
    if (!credentials) return;
    try {
      setIsLoadingOtp(true);
      const { access_token, refresh_token } = await validateOtpUseCase({
        ...credentials,
        otp: data.otp,
      });
      setTokens(access_token, refresh_token);
      navigate('/');
    } catch (error) {
      console.error('OTP validation failed', error);
    } finally {
      setIsLoadingOtp(false);
    }
  };

  const handleResendOtp = async () => {
    if (remainingTime > 0 || !credentials) return;
    try {
      setIsLoadingOtp(true);
      await loginUseCase(credentials);
      setRemainingTime(resendCooldown);
    } catch (error) {
      console.error('Failed to resend OTP', error);
    } finally {
      setIsLoadingOtp(false);
    }
  };

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (remainingTime > 0) {
      timer = setInterval(() => {
        setRemainingTime((prevTime) => prevTime - 1);
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [remainingTime]);

  useEffect(() => {
    const sessionExpiredMessage = localStorage.getItem('sessionExpiredMessage');
    if (sessionExpiredMessage) {
      setShowSessionExpiredMessage(true);
      localStorage.removeItem('sessionExpiredMessage');
    }
  }, []);

  return (
    <AuthLayout>
      {/* emssage is session expired */}
      {showSessionExpiredMessage && (
        <Callout
          className="mb-4"
          color="red"
          icon={RiErrorWarningLine}
          title={i18n.t('login.errors.sessionExpiredTitle')}
        >
          {i18n.t('login.errors.sessionExpiredMessage')}
        </Callout>
      )}
      {/* LOGIN FORM */}
      <Form<LoginFormValues> onSubmit={handleLogin} methods={loginMethods}>
        <div className="flex flex-col justify-start items-baseline space-y-8">
          <div className="flex flex-col justify-start items-baseline space-y-2 w-full">
            <label
              htmlFor="email"
              className="text-sm font-accent text-gray-200 pl-1"
            >
              {i18n.t('login.username')}
            </label>
            <TextInput
              type="email"
              autoFocus
              disabled={isLoggedIn}
              placeholder=""
              {...loginMethods.register('email')}
              error={!!loginMethods.formState.errors.email}
              errorMessage={loginMethods.formState.errors.email?.message}
            />
          </div>
          <div className="flex flex-col justify-start items-baseline space-y-2 w-full">
            <label
              htmlFor="password"
              className="text-sm font-accent text-gray-200 pl-1"
            >
              {i18n.t('login.password')}
            </label>
            <TextInput
              type="password"
              disabled={isLoggedIn}
              placeholder=""
              {...loginMethods.register('password')}
              error={!!loginMethods.formState.errors.password}
              errorMessage={loginMethods.formState.errors.password?.message}
            />
          </div>
          <Button
            type="submit"
            id="login-submit"
            loading={isLoadingLogIn}
            disabled={isLoggedIn || isLoadingLogIn}
            variant="primary"
            className="w-full"
          >
            {i18n.t('login.submit')}
          </Button>
        </div>
      </Form>
      {/* OTP FORM */}
      {isLoggedIn ? (
        <Form<OtpFormValues> onSubmit={handleOtpSubmit} methods={otpMethods}>
          <div className="flex flex-col justify-between mt-6 pt-4 border-t space-y-8 border-revving-border dark:border-darkRevving-border">
            <div className="space-y-4 text-gray-200">
              <p>{i18n.t('login.otpSentence')}</p>
              <div className="flex flex-col justify-start items-baseline space-y-2 w-full">
                <label
                  htmlFor="otp"
                  className="text-sm font-accent text-gray-200 pl-1"
                >
                  {i18n.t('login.otp')}
                </label>
                <TextInput
                  type="text"
                  autoFocus
                  {...otpMethods.register('otp')}
                  error={!!otpMethods.formState.errors.otp}
                  errorMessage={otpMethods.formState.errors.otp?.message}
                />
              </div>
            </div>
            <Button
              type="submit"
              id="otp-submit"
              loading={isLoadingOtp}
              disabled={isLoadingOtp}
              variant="primary"
              className="w-full"
            >
              {i18n.t('login.otpSubmit')}
            </Button>
            <Button
              type="button"
              onClick={handleResendOtp}
              loading={isLoadingOtp}
              disabled={remainingTime > 0 || isLoadingOtp}
              variant="secondary"
              className="w-full mt-4"
            >
              {remainingTime > 0
                ? `${i18n.t('login.resendCooldown')} (${remainingTime}s)`
                : i18n.t('login.resend')}
            </Button>
          </div>
        </Form>
      ) : (
        <div className="flex flex-col justify-center items-center mt-6 pt-4 border-t border-revving-border dark:border-darkRevving-border gap-2">
          <p>
            <Link to={NAVBAR_SOURCES.forgotPassword} className="text-white">
              {i18n.t('login.forgot')}
            </Link>
          </p>
        </div>
      )}
    </AuthLayout>
  );
};

export default LoginPage;
